import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import api from '../constants/api';
import { formatTo12Hour, formatDateTime12Hour } from '../constants/time';
import { LOCALE_CACHE_KEY, applyDefaultTimezone, getStoredTimezone } from '../constants/tenancy';

const AppContext = React.createContext();
// Cleared on login and whenever a super admin switches tenant, because the
// time zone (and its offset) in it belongs to the active tenant.
const SESSION_LOCALE_CONFIG_KEY = LOCALE_CACHE_KEY;
const FALLBACK_LOCALE_CONFIG = {
  country: 'IN',
  countryName: 'India',
  timezone: 'Asia/Kolkata',
  utcOffset: '+05:30',
  currency: { code: 'INR', symbol: '₹', position: 'before' },
  tax: {
    mode: 'GST',
    rate: 18,
    lineItems: [
      { label: 'CGST (9%)', rate: 0.09 },
      { label: 'SGST (9%)', rate: 0.09 },
    ],
  },
  phone: { dialCode: '+91', maxLength: 10 },
  dateFormat: 'DD-MM-YYYY',
  timeFormat: 'hh:mm A',
  dateTimeFormat: 'DD-MM-YYYY hh:mm A',
  bookingIdPrefix: 'FN',
  quotationIdPrefix: 'FNQ',
  workstationIdPrefix: 'WS',
};

const normalizeLocaleConfig = (config) => {
  const source = config || {};
  const currency = source.currency || {};
  const tax = source.tax || {};
  const phone = source.phone || {};

  const configuredLineItems = Array.isArray(tax.lineItems)
    ? tax.lineItems
      .filter((lineItem) => lineItem && typeof lineItem === 'object')
      .map((lineItem) => ({
        ...lineItem,
        label: lineItem.label || tax.mode || FALLBACK_LOCALE_CONFIG.tax.mode,
        rate: Number.isFinite(Number(lineItem.rate)) ? Number(lineItem.rate) : 0,
      }))
    : [];

  return {
    ...FALLBACK_LOCALE_CONFIG,
    ...source,
    currency: {
      ...FALLBACK_LOCALE_CONFIG.currency,
      ...currency,
    },
    tax: {
      ...FALLBACK_LOCALE_CONFIG.tax,
      ...tax,
      rate: Number.isFinite(Number(tax.rate))
        ? Number(tax.rate)
        : FALLBACK_LOCALE_CONFIG.tax.rate,
      lineItems: configuredLineItems.length
        ? configuredLineItems
        : FALLBACK_LOCALE_CONFIG.tax.lineItems,
    },
    phone: {
      ...FALLBACK_LOCALE_CONFIG.phone,
      ...phone,
      maxLength: Number.isInteger(Number(phone.maxLength)) && Number(phone.maxLength) > 0
        ? Number(phone.maxLength)
        : FALLBACK_LOCALE_CONFIG.phone.maxLength,
    },
    timeFormat: source.timeFormat || FALLBACK_LOCALE_CONFIG.timeFormat,
    dateTimeFormat: source.dateTimeFormat || `${source.dateFormat || FALLBACK_LOCALE_CONFIG.dateFormat} ${source.timeFormat || FALLBACK_LOCALE_CONFIG.timeFormat}`,
  };
};

const formatCurrencyAmount = (amount, localeConfig) => {
  const value = amount === '' || amount == null ? '' : amount;
  const { symbol, position } = localeConfig.currency;
  return position === 'after' ? `${value} ${symbol}` : `${symbol}${value}`;
};

const getCachedLocaleConfig = () => {
  try {
    const cachedConfig = window.sessionStorage.getItem(SESSION_LOCALE_CONFIG_KEY);
    return cachedConfig ? normalizeLocaleConfig(JSON.parse(cachedConfig)) : null;
  } catch (error) {
    console.warn('Unable to read cached locale config; requesting a fresh config.', error);
    return null;
  }
};

const AppProvider = ({ children }) => {
  AppProvider.propTypes = {
    children: PropTypes.any,
  };
  const [lang, setLang] = useState('en');
  const [loggedInuser, setLoggedInUser] = useState({});
  const [localeConfig, setLocaleConfig] = useState(() => getCachedLocaleConfig() || normalizeLocaleConfig({
    ...FALLBACK_LOCALE_CONFIG,
    timezone: getStoredTimezone() || FALLBACK_LOCALE_CONFIG.timezone,
  }));
  const [localeConfigLoading, setLocaleConfigLoading] = useState(() => !getCachedLocaleConfig());
 
  const getUser = () => {
    let user = localStorage.getItem('user');
    if(user){
      user = JSON.parse(user);
      setLoggedInUser(user);
    }
    
  };
  React.useEffect(() => {
    getUser();
    return () => {
      getUser();
    };
  }, []);

  // Every moment() in the app renders in the tenant's zone.
  React.useEffect(() => {
    applyDefaultTimezone(localeConfig.timezone);
  }, [localeConfig.timezone]);

  React.useEffect(() => {
    const cachedConfig = getCachedLocaleConfig();
    if (cachedConfig) {
      setLocaleConfig(cachedConfig);
      setLocaleConfigLoading(false);
      return undefined;
    }

    let active = true;
    api
      .get('/config/locale')
      .then((res) => {
        if (!active) return;
        if (!res.data?.success || !res.data?.data) {
          throw new Error('Locale config response did not contain locale data.');
        }
        const normalizedConfig = normalizeLocaleConfig(res.data.data);
        window.sessionStorage.setItem(SESSION_LOCALE_CONFIG_KEY, JSON.stringify(normalizedConfig));
        setLocaleConfig(normalizedConfig);
      })
      .catch((err) => {
        console.warn('Unable to load locale config; using India fallback.', err);
      })
      .finally(() => {
        if (active) {
          setLocaleConfigLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const localeContextValue = useMemo(() => {
    const configuredTaxRate = Number(localeConfig.tax.rate);
    const taxRateDecimal = Number.isFinite(configuredTaxRate) ? configuredTaxRate / 100 : 0;
    const toFiniteAmount = (amount) => {
      const parsedAmount = Number.parseFloat(amount || 0);
      return Number.isFinite(parsedAmount) ? parsedAmount : 0;
    };
    const getTaxAmountExclusive = (amount) => (toFiniteAmount(amount) * taxRateDecimal);
    const getTaxAmountFromInclusive = (amount) => {
      const baseAmount = toFiniteAmount(amount);
      const denominator = 1 + taxRateDecimal;
      return taxRateDecimal && denominator !== 0
        ? (baseAmount * taxRateDecimal) / denominator
        : 0;
    };
    const getAmountWithTax = (amount) => toFiniteAmount(amount) + getTaxAmountExclusive(amount);
    const getTaxLineItemAmounts = (amount) => {
      const baseAmount = toFiniteAmount(amount);

      return localeConfig.tax.lineItems.map((lineItem) => ({
        ...lineItem,
        amount: baseAmount * Number(lineItem.rate || 0),
      }));
    };

    return {
      localeConfig,
      localeConfigLoading,
      country: localeConfig.country,
      countryName: localeConfig.countryName,
      timezone: localeConfig.timezone,
      utcOffset: localeConfig.utcOffset,
      currencyCode: localeConfig.currency.code,
      currencySymbol: localeConfig.currency.symbol,
      currencyPosition: localeConfig.currency.position,
      taxMode: localeConfig.tax.mode,
      taxRate: localeConfig.tax.rate,
      taxRateDecimal,
      taxLineItems: localeConfig.tax.lineItems,
      dialCode: localeConfig.phone.dialCode,
      phoneMaxLength: localeConfig.phone.maxLength,
      dateFormat: localeConfig.dateFormat,
      timeFormat: localeConfig.timeFormat || 'hh:mm A',
      dateTimeFormat: localeConfig.dateTimeFormat || `${localeConfig.dateFormat} hh:mm A`,
      bookingIdPrefix: localeConfig.bookingIdPrefix,
      quotationIdPrefix: localeConfig.quotationIdPrefix,
      formatCurrency: (amount) => formatCurrencyAmount(amount, localeConfig),
      formatTime: (timeVal) => formatTo12Hour(timeVal),
      formatDateTime: (dateTime, df) => formatDateTime12Hour(dateTime, df || localeConfig.dateFormat),
      getTaxAmountExclusive,
      getTaxAmountFromInclusive,
      getAmountWithTax,
      getTaxLineItemAmounts,
    };
  }, [localeConfig, localeConfigLoading]);

  return (
    <AppContext.Provider
      value={{
        loggedInuser,
        setLoggedInUser,
        lang,
        setLang,
        ...localeContextValue,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const useLocaleConfig = () => useContext(AppContext);

export { AppProvider, useLocaleConfig, AppContext as default };
